import { ExtrudeGeometry, Shape } from 'three';

/* A board with rounded corners.
 *
 * BoxGeometry cannot do this, and the boards are the one part whose corners
 * you actually see -- they are the book's silhouette. The text block behind
 * them stays square-cut, which is what a real one is.
 *
 * The catch is the UVs. The front board carries the cover art through a UV
 * transform derived against BoxGeometry's own layout, so anything here has to
 * reproduce that layout exactly rather than approximately, or the artwork
 * lands rotated or mirrored. BoxGeometry's +y face is:
 *
 *     u = (x + length / 2) / length      v = (width / 2 - z) / width
 *
 * so that is written out below rather than left to an extrude UV generator.
 * Note the v axis runs BACKWARDS along z -- buildPlane passes the +y face a
 * vdir of -1. Reading it the other way puts the cover art on mirrored and the
 * groove down the fore-edge, which is exactly what it did.
 */

const CORNER_SEGMENTS = 4;

function roundedRect(length, width, radius) {
  const shape = new Shape();
  const x = length / 2;
  const y = width / 2;
  const r = Math.min(radius, Math.min(length, width) / 2);

  shape.moveTo(-x + r, -y);
  shape.lineTo(x - r, -y);
  shape.quadraticCurveTo(x, -y, x, -y + r);
  shape.lineTo(x, y - r);
  shape.quadraticCurveTo(x, y, x - r, y);
  shape.lineTo(-x + r, y);
  shape.quadraticCurveTo(-x, y, -x, y - r);
  shape.lineTo(-x, -y + r);
  shape.quadraticCurveTo(-x, -y, -x + r, -y);
  return shape;
}

/**
 * @param length  along x, the book's height
 * @param thick   along y, the board's thickness
 * @param width   along z, the cover's width
 *
 * Materials are grouped [top (+y), bottom (-y), edge].
 */
export function boardGeometry(length, thick, width, radius) {
  /* Extrude runs along +z, so the shape is authored in the plane the board
     will end up lying in and then tipped over: the extrusion axis becomes the
     board's thickness. */
  const geometry = new ExtrudeGeometry(roundedRect(length, width, radius), {
    depth: thick,
    bevelEnabled: false,
    curveSegments: CORNER_SEGMENTS,
  });
  geometry.translate(0, 0, -thick / 2);
  geometry.rotateX(-Math.PI / 2);
  geometry.computeVertexNormals();

  const pos = geometry.attributes.position;
  const nor = geometry.attributes.normal;
  const uv = geometry.attributes.uv;

  for (let i = 0; i < pos.count; i++) {
    uv.setXY(
      i,
      (pos.getX(i) + length / 2) / length,
      (width / 2 - pos.getZ(i)) / width
    );
  }
  uv.needsUpdate = true;

  /* Split into the three material groups by facing. ExtrudeGeometry emits the
     caps before the wall and never interleaves them, so scanning triangles in
     order yields contiguous runs and no group is ever fragmented. */
  geometry.clearGroups();
  const faceOf = tri => {
    const n = nor.getY(tri * 3);
    return n > 0.5 ? 0 : n < -0.5 ? 1 : 2;
  };
  const triangles = pos.count / 3;
  let runStart = 0;
  let runFace = faceOf(0);
  for (let tri = 1; tri <= triangles; tri++) {
    const face = tri < triangles ? faceOf(tri) : -1;
    if (face !== runFace) {
      geometry.addGroup(runStart * 3, (tri - runStart) * 3, runFace);
      runStart = tri;
      runFace = face;
    }
  }
  return geometry;
}
